import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { propertyAPI } from '../services/propertyAPI';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useToast } from '../contexts/ToastContext';
import {
  PROPERTY_TYPES,
  PRICE_TYPES,
  RENTAL_TYPE_OPTIONS,
  PROPERTY_AGE,
  FURNISHING_OPTIONS,
  PARKING_OPTIONS,
  FACING_OPTIONS,
  AREA_UNIT_OPTIONS,
  AMENITIES,
  STATUS_OPTIONS,
} from '../utils/propertyConstants';

const EditProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: null, // 'image' or 'video'
    itemKey: null,
    isDeleting: false
  });

  const [formData, setFormData] = useState({
    title: '',
    price: '',
    priceType: 'per_month',
    address: '',
    locality: '',
    city: '',
    state: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    propertyAge: '<3',
    bhk: '',
    bathrooms: '',
    balconies: '',
    furnishing: 'unfurnished',
    parking: 'not_available',
    propertyType: 'apartment',
    facing: 'north',
    area: '',
    areaUnit: 'sqr_ft',
    floor: '',
    totalFloors: '',
    birdNet: false,
    pipedGas: false,
    infoBanner: '',
    description: '',
    amenities: [],
    contactPhone: '',
    contactEmail: '',
    contactWhatsapp: '',
    tags: '',
    status: 'active',
  });

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [video, setVideo] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [existingVideo, setExistingVideo] = useState(null);

  useEffect(() => {
    fetchProperty();
    // eslint-disable-next-line
  }, [id]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      const response = await propertyAPI.getPropertyById(id);
      
      if (response.success) {
        const property = response.data;
        
        // Pre-fill form data
        setFormData({
          title: property.title || '',
          price: property.price || '',
          priceType: property.priceType || 'per_month',
          rentalType: property.rentalType || 'longTerm',
          address: property.address || '',
          locality: property.locality || '',
          city: property.city || '',
          state: property.state || '',
          postalCode: property.postalCode || '',
          latitude: property.latitude ?? '',
          longitude: property.longitude ?? '',
          propertyAge: property.propertyAge || '<3',
          bhk: property.bhk || '',
          bathrooms: property.bathrooms || '',
          balconies: property.balconies || '',
          furnishing: property.furnishing || 'unfurnished',
          parking: property.parking || 'not_available',
          propertyType: property.propertyType || 'apartment',
          facing: property.facing || 'north',
          area: property.area || '',
          areaUnit: property.areaUnit || 'sqr_ft',
          floor: property.floor || '',
          totalFloors: property.totalFloors || '',
          birdNet: property.birdNet || false,
          pipedGas: property.pipedGas || false,
          infoBanner: property.infoBanner || '',
          description: property.description || '',
          amenities: (property.amenities || []).map(a => a === 'clubhouse' ? 'club_house' : a === 'medical_facility' ? 'medical' : a),
          contactPhone: property.contactPhone || '',
          contactEmail: property.contactEmail || '',
          contactWhatsapp: property.contactWhatsapp || '',
          tags: property.tags ? property.tags.join(', ') : '',
          status: property.status || 'active',
        });

        setExistingImages(property.media?.images || []);
        setExistingVideo(property.media?.video || null);
        
        console.log('📦 Property loaded:', property._id || property.id);
        console.log('🖼️ Existing images:', property.media?.images);
        console.log('🎬 Existing video:', property.media?.video);
      }
    } catch (err) {
      showError('Failed to load property details.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'amenities') {
        if (checked) {
          setFormData({ ...formData, amenities: [...formData.amenities, value] });
        } else {
          setFormData({ ...formData, amenities: formData.amenities.filter(a => a !== value) });
        }
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length + existingImages.length > 10) {
      showError('Maximum 10 images allowed (including existing)');
      return;
    }

    setImages([...images, ...files]);

    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews([...imagePreviews, ...previews]);
  };

  const removeNewImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  // Open delete modal
  const openDeleteModal = (type, itemKey = null) => {
    console.log('🔴 Opening delete modal for:', type, 'Key:', itemKey);
    setDeleteModal({
      isOpen: true,
      type: type,
      itemKey: itemKey,
      isDeleting: false
    });
  };

  // Close delete modal
  const closeDeleteModal = () => {
    if (!deleteModal.isDeleting) {
      setDeleteModal({
        isOpen: false,
        type: null,
        itemKey: null,
        isDeleting: false
      });
    }
  };

  // Confirm deletion
  const confirmDelete = async () => {
    console.log('🗑️ Confirm delete called');
    console.log('Delete modal state:', deleteModal);
    
    setDeleteModal({ ...deleteModal, isDeleting: true });

    try {
      if (deleteModal.type === 'image') {
        console.log('🖼️ Deleting image:', deleteModal.itemKey, 'from property:', id);
        const response = await propertyAPI.deletePropertyImage(id, deleteModal.itemKey);
        console.log('✅ Image delete response:', response);
        
        setExistingImages(existingImages.filter(img => img.key !== deleteModal.itemKey));
        showSuccess(response.message || 'Image deleted successfully from AWS S3');
      } else if (deleteModal.type === 'video') {
        console.log('🎬 Deleting video from property:', id);
        const response = await propertyAPI.deletePropertyVideo(id);
        console.log('✅ Video delete response:', response);
        
        setExistingVideo(null);
        showSuccess(response.message || 'Video deleted successfully from AWS S3');
      }
      
      closeDeleteModal();
    } catch (err) {
      console.error('❌ Delete error:', err);
      console.error('Error details:', err.response?.data || err.message);
      showError(err.response?.data?.message || err.message || `Failed to delete ${deleteModal.type}`);
      setDeleteModal({ ...deleteModal, isDeleting: false });
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showError('Video size must be less than 10MB');
        return;
      }
      setVideo(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      const formDataToSend = new FormData();

      // Append all text fields
      Object.keys(formData).forEach(key => {
        if (key === 'amenities') {
          formData[key].forEach(amenity => {
            formDataToSend.append('amenities[]', amenity);
          });
        } else if (key === 'tags') {
          if (formData[key]) {
            const tagsArray = formData[key].split(',').map(t => t.trim());
            tagsArray.forEach(tag => {
              formDataToSend.append('tags[]', tag);
            });
          }
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Append new images
      images.forEach((image) => {
        formDataToSend.append('images', image);
      });

      // Append video
      if (video) {
        formDataToSend.append('video', video);
      }

      const response = await propertyAPI.updateProperty(id, formDataToSend);

      if (response.success) {
        showSuccess(response.message || 'Property updated successfully!');
        setTimeout(() => {
          navigate(`/property/${id}`);
        }, 1000);
      }
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Failed to update property. Please try again.');
      console.error(err);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg-light pt-20">
      {/* Fixed Header */}
      <Header transparent={true} />

      {/* Page Title */}
      <div className="glass backdrop-blur-lg border-b border-purple-100 py-4 fixed top-[80px] left-0 right-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Edit Property</h1>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              console.log('Top Cancel clicked');
              navigate(`/property/${id}`);
            }}
            className="text-gray-700 hover:text-primary font-semibold transition flex items-center group cursor-pointer z-50 pointer-events-auto"
          >
            <svg className="w-5 h-5 mr-1 group-hover:rotate-180 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-20 relative z-10 pointer-events-auto">
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-6 space-y-6 fade-in relative z-10 pointer-events-auto">
          {/* Basic Information */}
          <div className="relative z-20 pointer-events-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Property Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  maxLength={200}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Property Type *</label>
                <select name="propertyType" value={formData.propertyType} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  {PROPERTY_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Price *</label>
                <input type="number" name="price" value={formData.price} onChange={handleChange} required min="0" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Price Type *</label>
                <select name="priceType" value={formData.priceType} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  {PRICE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Rental Type *</label>
                <select name="rentalType" value={formData.rentalType} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  {RENTAL_TYPE_OPTIONS.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status *</label>
                <select name="status" value={formData.status} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  {STATUS_OPTIONS.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="relative z-20 pointer-events-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Location</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Address *</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange} required maxLength={500} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Locality *</label>
                <input type="text" name="locality" value={formData.locality} onChange={handleChange} required maxLength={100} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} required maxLength={100} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">State *</label>
                <input type="text" name="state" value={formData.state} onChange={handleChange} required maxLength={100} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Postal Code *</label>
                <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} required maxLength={20} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Latitude</label>
                <input type="number" name="latitude" value={formData.latitude} onChange={handleChange} step="any" min="-90" max="90" placeholder="e.g. 12.9716" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Longitude</label>
                <input type="number" name="longitude" value={formData.longitude} onChange={handleChange} step="any" min="-180" max="180" placeholder="e.g. 77.5946" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="relative z-20 pointer-events-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Specifications</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">BHK *</label>
                <input type="number" name="bhk" value={formData.bhk} onChange={handleChange} required min="1" max="10" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Bathrooms *</label>
                <input type="number" name="bathrooms" value={formData.bathrooms} onChange={handleChange} required min="1" max="10" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Balconies *</label>
                <input type="number" name="balconies" value={formData.balconies} onChange={handleChange} required min="0" max="10" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Area *</label>
                <input type="number" name="area" value={formData.area} onChange={handleChange} required min="1" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Area Unit *</label>
                <select name="areaUnit" value={formData.areaUnit} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  {AREA_UNIT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Property Age *</label>
                <select name="propertyAge" value={formData.propertyAge} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  {PROPERTY_AGE.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Furnishing *</label>
                <select name="furnishing" value={formData.furnishing} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  {FURNISHING_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Parking *</label>
                <select name="parking" value={formData.parking} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  {PARKING_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Facing *</label>
                <select name="facing" value={formData.facing} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  {FACING_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Floor</label>
                <input type="number" name="floor" value={formData.floor} onChange={handleChange} min="1" max="200" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Floor number" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Total Floors</label>
                <input type="number" name="totalFloors" value={formData.totalFloors} onChange={handleChange} min="1" max="200" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Total floors in building" />
              </div>
              <div className="flex items-center gap-4 md:col-span-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" name="birdNet" checked={formData.birdNet} onChange={handleChange} className="w-4 h-4 text-primary focus:ring-primary border-gray-300 rounded" />
                  <span className="text-sm text-gray-700">Bird Net</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" name="pipedGas" checked={formData.pipedGas} onChange={handleChange} className="w-4 h-4 text-primary focus:ring-primary border-gray-300 rounded" />
                  <span className="text-sm text-gray-700">Piped Gas</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Info Banner / Notice (max 30 chars)</label>
                <input type="text" name="infoBanner" value={formData.infoBanner} onChange={handleChange} maxLength={30} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="e.g. Available from March" />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="relative z-20 pointer-events-auto">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea name="description" value={formData.description} onChange={handleChange} required maxLength={2000} rows="5" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
          </div>

          {/* Amenities */}
          <div className="relative z-20 pointer-events-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Amenities</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {AMENITIES.slice(0, 24).map(amenity => (
                <label key={amenity.value} className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" name="amenities" value={amenity.value} checked={formData.amenities.includes(amenity.value)} onChange={handleChange} className="w-4 h-4 text-primary focus:ring-primary border-gray-300 rounded" />
                  <span className="text-sm text-gray-700">{amenity.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Existing Video */}
          {existingVideo && (
            <div className="relative z-20 pointer-events-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Existing Video</h2>
              <div className="relative w-full">
                <video src={existingVideo.url} controls className="w-full h-64 object-cover rounded-lg" />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🔴 Delete video button clicked');
                    openDeleteModal('video');
                  }}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 shadow-lg transition z-50 cursor-pointer pointer-events-auto"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Click delete to permanently remove from AWS S3
              </p>
            </div>
          )}

          {/* Existing Images */}
          {existingImages.length > 0 && (
            <div className="relative z-20 pointer-events-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Existing Images</h2>
              <div className="grid grid-cols-4 gap-2">
                {existingImages.map((image, index) => (
                  <div key={index} className="relative group">
                    <img src={image.url} alt={`Existing ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('🔴 Delete button clicked for image:', image.key);
                        openDeleteModal('image', image.key);
                      }}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-lg transition opacity-0 group-hover:opacity-100 z-50 cursor-pointer pointer-events-auto"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Hover over image and click × to delete
              </p>
            </div>
          )}

          {/* New Images */}
          <div className="relative z-20 pointer-events-auto">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Add New Images</label>
            <input type="file" accept="image/*" multiple onChange={handleImageChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
            
            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-4">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative">
                    <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                    <button 
                      type="button" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeNewImage(index);
                      }} 
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 z-50 cursor-pointer pointer-events-auto shadow-lg"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upload New Video */}
          <div className="relative z-20 pointer-events-auto">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {existingVideo ? 'Replace Video' : 'Add Property Video'} (Optional, Max 10MB)
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {video && (
              <div className="mt-2 text-sm text-green-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                New video selected: {video.name}
              </div>
            )}
            {existingVideo && !video && (
              <p className="text-sm text-gray-600 mt-2">Current video will be kept if no new video is uploaded</p>
            )}
          </div>

          {/* Contact Information */}
          <div className="relative z-20 pointer-events-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Phone *</label>
                <input type="tel" name="contactPhone" value={formData.contactPhone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Email *</label>
                <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">WhatsApp Number (Optional)</label>
                <input type="tel" name="contactWhatsapp" value={formData.contactWhatsapp} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 relative z-50 pointer-events-auto">
            <button 
              type="button" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Cancel clicked');
                navigate(`/property/${id}`);
              }} 
              disabled={submitLoading} 
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition disabled:opacity-50 cursor-pointer shadow-sm hover:shadow-md"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={submitLoading} 
              className="px-6 py-3 bg-gradient-to-r from-primary to-purple-600 text-white rounded-lg font-bold hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all duration-200 btn-ripple"
            >
              {submitLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </span>
              ) : (
                'Update Property'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={deleteModal.type === 'video' ? 'Delete Video' : 'Delete Image'}
        message={
          deleteModal.type === 'video'
            ? 'Are you sure you want to delete this video? This will permanently remove the file from AWS S3 storage.'
            : 'Are you sure you want to delete this image? This will permanently remove the file from AWS S3 storage.'
        }
        confirmText="Delete"
        isDeleting={deleteModal.isDeleting}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default EditProperty;

